import { useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnFiltersState,
} from "@tanstack/react-table";
import {
  Loader2,
  Plus,
  Trash,
  Pencil,
  Eye,
  Calendar,
  MoreHorizontal,
  File,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import {
  useEquipment,
  useDeleteEquipment,
  useUserDetails,
  useEquipmentCertificate,
} from "@/hooks";
import { Equipment } from "@/api/get-equipment";
import { Link, Navigate } from "react-router";
import {
  EquipmentBadge,
  EquipmentBadgeVariants,
} from "@/components/equipment-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function EquipmentPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // query
  const {
    data: equipment,
    isLoading,
    refetch: refetchEquipment,
  } = useEquipment();
  const { data: user } = useUserDetails();
  const { mutateAsync: getEquipmentCertificate } = useEquipmentCertificate();
  // mutations
  const deleteEquipment = useDeleteEquipment();

  // Table columns
  const columns: ColumnDef<Equipment>[] = [
    {
      accessorKey: "serialNumber",
      header: "Número de Serie",
    },
    {
      accessorFn: (row) => (
        <div className="grid gap-0">
          <p className="text-sm font-medium">{row.model}</p>
          <p className="text-xs text-muted-foreground">{row.brand}</p>
        </div>
      ),
      header: "Modelo",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "equipmentType",
      header: "Tipo de Equipo",
      cell: ({ row }) => {
        const equipmentType = row.getValue("equipmentType");
        return (
          <EquipmentBadge variant={equipmentType as EquipmentBadgeVariants} />
        );
      },
    },
    {
      accessorKey: "calibratedAt",
      header: "Calibración",
      accessorFn: (row) => {
        const calibratedAt = row?.calibratedAt ? row?.calibratedAt : "---";
        const nextCalibration = row?.nextCalibration ? (
          <div className="flex items-center gap-1">
            <Calendar size={16} /> {row?.nextCalibration}
          </div>
        ) : (
          "---"
        );

        return (
          <div className="grid gap-0">
            <p className="text-sm">{calibratedAt}</p>
            <p className="text-xs text-muted-foreground">{nextCalibration}</p>
          </div>
        );
      },
      cell: (info) => info.getValue(),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const equipment = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Abrir menú</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-foreground">
              <DropdownMenuLabel>Acciones</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={`/equipos/${equipment.id}`}>
                  <Eye className="size-4" />
                  Ver detalles
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!equipment?.certificatePath}
                onClick={async () => {
                  const url = await getEquipmentCertificate({
                    equipmentId: equipment?.id ?? "",
                  });
                  window.open(url, "_blank");
                }}
              >
                <File className="size-4" />
                Descarga certificado
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/equipos/${equipment.id}/editar`}>
                  <Pencil className="size-4" />
                  Editar
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setSelectedEquipment(equipment);
                  setIsDeleteDialogOpen(true);
                }}
                className="text-destructive focus:text-destructive gap-2"
              >
                <Trash className="size-4 text-destructive" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: equipment || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  if (user?.type === "anonymous") {
    return <Navigate to="/inicia-sesion?redirect=equipos" />;
  }

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold">Equipos</h1>
          <p className="text-xs text-muted-foreground">Administra tu equipos</p>
        </div>
      </div>
      <Separator />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Input
            placeholder="Buscar por número de serie..."
            value={
              (table.getColumn("serialNumber")?.getFilterValue() as string) ??
              ""
            }
            onChange={(event) =>
              table
                .getColumn("serialNumber")
                ?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <Button className="gap-2" asChild variant="success">
            <Link to="/equipos/crear">
              <Plus className="size-4" />
              Agregar Equipo
            </Link>
          </Button>
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No se encontraron equipos.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Siguiente
          </Button>
        </div>
      </div>
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="hidden">
            <Trash className="size-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              el equipo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-destructive text-destructive hover:bg-transparent hover:text-destructive">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (selectedEquipment) {
                  await deleteEquipment.mutateAsync({
                    id: selectedEquipment?.id,
                  });
                  refetchEquipment();
                }
              }}
              disabled={deleteEquipment.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteEquipment.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
